import React, { useLayoutEffect, useRef, useState } from 'react'

import { ArrowLeftIcon } from 'lucide-react'
import { Box, Stack, Typography, SxProps, Theme } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { SideNav, SideNavItem } from '../SideNav'

export type SectionNavItem = SideNavItem

export interface SectionNavProps {
  /** Items rendered in order. Items with `visible === false` are skipped. */
  items: SectionNavItem[]
  /** `value` of the currently-active item (drives the active row / sliding pill). */
  value: string
  /** Called with an item's `value` when it's clicked. */
  onChange: (value: string) => void
  /** When provided, a Back affordance is shown — always visible at both breakpoints. */
  onBack?: () => void
  /** Back affordance label (wide screen only; the small-screen row shows the arrow). */
  backLabel?: string
  /** Breakpoint at and above which the vertical rail is used; below it folds to the row. Default `md`. */
  breakpoint?: 'sm' | 'md' | 'lg'
  /** Extra styles merged onto the vertical rail's `SideNav`. */
  sx?: SxProps<Theme>
}

const hideScrollbar = {
  scrollbarWidth: 'none' as const,
  '&::-webkit-scrollbar': { display: 'none' },
}

/**
 * Responsive in-page table-of-contents. On `md+` it's a vertical left rail (the
 * shared `SideNav`); below the breakpoint it folds into a single horizontal,
 * scrollable pill row (the pattern from the internal Components lab) so the TOC —
 * and the Back affordance — stay reachable on small screens instead of vanishing.
 *
 * Presentational only: the parent owns active-section tracking (scroll-spy) and
 * passes the active `value`; this component renders both layouts and animates a
 * sliding pill under the active chip in the row variant.
 */
export const SectionNav = (props: SectionNavProps) => {
  const { items, value, onChange, onBack, backLabel = 'Back', breakpoint = 'md', sx } = props
  const theme = useTheme()

  const visibleItems = items.filter(item => item.visible !== false)

  // Sliding pill under the active chip (row variant). Measured from the active
  // button's offset within the scroll container; also scrolls it into view.
  const rowRef = useRef<HTMLDivElement>(null)
  const [pill, setPill] = useState({ left: 0, width: 0, visible: false })

  useLayoutEffect(() => {
    const container = rowRef.current
    if (!container) return
    const activeEl = container.querySelector(`[data-section-id="${value}"]`) as HTMLElement | null
    if (!activeEl) {
      setPill(p => ({ ...p, visible: false }))
      return
    }
    setPill({ left: activeEl.offsetLeft, width: activeEl.offsetWidth, visible: true })
    activeEl.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, visibleItems.map(i => i.value).join('|')])

  const Back =
    onBack &&
    (() => (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={onBack}
        sx={{ cursor: 'pointer', color: 'text.secondary', flexShrink: 0 }}
      >
        <ArrowLeftIcon strokeWidth={1} size={20} />
        {backLabel && (
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            {backLabel}
          </Typography>
        )}
      </Stack>
    ))

  return (
    <>
      {/* ── Wide: vertical left rail ─────────────────────────────────── */}
      <Box
        sx={{
          width: 200,
          flexShrink: 0,
          display: { xs: 'none', [breakpoint]: 'block' },
        }}
      >
        <Box sx={{ position: 'sticky', top: 24 }}>
          {Back && (
            <Box sx={{ mb: 4, ml: -0.5 }}>
              <Back />
            </Box>
          )}
          {/* RAIL, not the pill menu. A SectionNav is scroll-spy: the active row is REPORTED
              by scroll position, never chosen — so it wants "you are here", which is what a
              marker outside the text box says, rather than "this is selected", which is what a
              filled surface says. The pill also mis-weights it on a FORM: at 48px and filled it
              was the highest-contrast element on the page after the primary button, louder than
              the fields that are the actual work. (On a docs page a nav earns that weight,
              because navigating IS the activity. Here it doesn't.)

              This leaves the platform one clean rule: pill = a destination you choose (Settings,
              Labs, which keep `SideNav`'s default), rail = a position you're at (this, and the
              docs contents list). Before, both rendered identically while meaning different
              things. */}
          <SideNav variant="rail" items={visibleItems} value={value} onChange={onChange} sx={sx} />
        </Box>
      </Box>

      {/* ── Small: horizontal scroll pill row (Back pinned left) ─────── */}
      <Box
        sx={{
          display: { xs: 'flex', [breakpoint]: 'none' },
          alignItems: 'center',
          gap: 2,
          width: '100%',
          // Sticky so the TOC stays reachable while the long form scrolls.
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.default',
          py: 1.5,
          // Mask the gap above the sticky row (the scroll container's top padding) with
          // the page background, so form content scrolling up doesn't peek through above
          // the nav. Same-colour, so it's invisible when the row is at rest at the top.
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: '100%',
            height: 96,
            bgcolor: 'background.default',
          },
        }}
      >
        {Back && <Back />}
        <Box
          ref={rowRef}
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            overflowX: 'auto',
            minWidth: 0,
            flex: 1,
            ...hideScrollbar,
          }}
        >
          {/* Sliding active pill */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: 0,
              height: 34,
              width: pill.width,
              transform: `translate(${pill.left}px, -50%)`,
              bgcolor: theme.palette.primary[50],
              borderRadius: 2,
              transition: 'transform 260ms ease, width 260ms ease, opacity 150ms',
              opacity: pill.visible ? 1 : 0,
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
          {visibleItems.map(item => {
            const selected = item.value === value
            return (
              <Box
                component="button"
                key={item.value}
                data-section-id={item.value}
                onClick={() => onChange(item.value)}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  fontFamily: 'inherit',
                  fontSize: 15,
                  fontWeight: selected ? 700 : 500,
                  color: selected ? 'text.primary' : 'text.secondary',
                  transition: 'color 200ms ease',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                {item.label}
                {item.endAdornment}
              </Box>
            )
          })}
        </Box>
      </Box>
    </>
  )
}
